<template>
  <div :data-test-path="'img-card-'+imageIndex" @dragstart.prevent
       class="mb-2 mt-2 justify-content-center d-flex align-items-center">
    <div class="card border-0 ml-2 mr-1 custom-container"
         :class="{ selected: selected }">

      <div v-if="!image.loading" class="star-wrapper" @click.stop="toggleStar">
        <div :class="starState"></div>
      </div>

      <div v-if="image.loading" class="spinner-container">
        <b-spinner label="Generowanie..."></b-spinner>
      </div>

      <img v-else class="card-img-top custom-img"
           :src="imageSrc"
           :alt="imageAlt"
           draggable="false"
           @click="openDialog">

      <div v-if="!image.loading" class="checkbox-wrapper" @click.stop="toggleToWishlist">
        <input type="checkbox" class="checkbox checkbox-light" :checked="isWishlist"
               @click.stop="toggleToWishlist">
        <span class="font-weight-bold">
          {{
            $tr('inspiration-add-to-wishlist-checkbox', 'Add to Wishlist')
          }}
        </span>
      </div>
    </div>

    <transition name="dialog-fade">
      <div v-if="showDialog" class="dialog-overlay">
        <div class="dialog-content">


          <div v-if="imagePreview" class="dialog-image-wrapper">
            <img :src="imageSrc" :alt="imageAlt" class="dialog-img" draggable="false">
          </div>
          <div v-if="imagePreview" class="dialog-buttons">

            <button type="button" @click="closeDialog" aria-label="Close"
                    class="close d-none d-sm-inline-block dialog-close"
                    style="pointer-events: auto;">
              <span aria-hidden="true">×</span>
            </button>

            <b-row class="mb-2 flex-column justify-content-center align-items-center mr-2 ml-2">
              <div class="custom-buttons-container">
                <b-button @click="onAISketch" class="options-button">
                  <img src="https://silkplm.com/wp-content/uploads/2025/02/magic-wand-3.png" class="custom-icon"
                       alt="Custom icon with magic wand for AI Retouch option">

                  {{
                    $tr('inspiration-dialog-button-ai-retouch', 'AI Retouch')
                  }}
                </b-button>

                <b-button @click="onSketch2Photo" class="options-button">
                  <img src="https://silkplm.com/wp-content/uploads/2025/02/Sketch2img-1.png" class="custom-icon"
                       alt="Custom icon with shirt for Sketch to Image option">

                  {{
                    $tr('inspiration-dialog-button-sketch-to-photo', 'Sketch to Image')
                  }}
                </b-button>

                <!--              <b-button @click="onDraft2Project" class="options-button">-->
                <!--                {{-->
                <!--                  $tr('inspiration-dialog-button-draft-to-project', 'Draft to project')-->
                <!--                }}-->
                <!--              </b-button>-->
              </div>
            </b-row>

            <b-row class="flex-column justify-content-center align-items-center mr-2 ml-2 mb-2">
              <div class="custom-buttons-container pt-1 pb-1">
                <b-button @click="editImage" class="options-button">
                  <font-awesome-icon :icon="['far', 'pencil']" class="upload-icon mr-2"/>
                  {{
                    $tr('inspiration-dialog-button-edit', 'Edit Image')
                  }}
                </b-button>
                <b-button @click="saveImage" class="options-button">
                  <font-awesome-icon :icon="['fas', 'download']" class="upload-icon mr-2"/>
                  {{
                    $tr('inspiration-dialog-button-download', 'Download Image')
                  }}
                </b-button>
                <b-button @click="deleteImage" class="options-button">
                  <font-awesome-icon :icon="['far', 'trash']" class="upload-icon mr-2"/>
                  {{
                    $tr('inspiration-dialog-button-delete', 'Delete Image')
                  }}
                </b-button>
              </div>
            </b-row>

            <div v-if="image.customData && image.customData.prompt"
                 class="custom-buttons-container ml-2 mr-2 mb-2 pt-2 pb-2 pr-2 pl-2"
                 style="width:230px; padding: 9px 13px !important;">
              <div class="prompt-container" style="max-height: 73px; overflow-y: scroll;">
                <span>{{ image.customData.prompt }}</span>
              </div>
            </div>

            <div v-if="showTagSection"
                 class="custom-buttons-container ml-2 mr-2 pt-1 pb-1 pr-2 pl-2"
                 style="width:230px; "
                 :style="image.customData && !image.customData.prompt ? 'position: absolute; bottom: 16px;' : ''">
              <div class="tags-container">
                <div
                    v-for="(tag, index) in image.customData.imageTagList"
                    :key="index"
                    class="tag-item"
                    v-if="tag.name !== 'Main Picture'"
                >
                  <div class="icon-box" v-if="tagIconMap[tag.name.replace(/\s+/g, '_')]">
                    <font-awesome-icon
                        :icon="tagIconMap[tag.name.replace(/\s+/g, '_')]"
                    />
                  </div>
                  {{ capitalize(tag.name) }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import ActivityManager from "@/activity/ActivityManager";
import PinturaImageEditor from "@/components/img/PinturaImageEditor.vue";
import Rpc from "@/Rpc";
import FileInRepository from "@/server/com/silkplm/model/files/FileInRepository";
import DialogManager from "@/utils/DialogManager";
import {AISketch, draft2Project, sketch2Photo} from "@/components/img/AIService";
import {cloneEntity} from "@/utils/Objects";
import ImageTag from "@/server/com/silkplm/model/plm/dictionaries/ImageTag";
import {
  base64ToFile,
  getImageFileFromUrl,
  replacePlaceholderWithImage,
  preparePlaceholder,
  setImageTag
} from "@/components/forms/renderers/inspirations/InspirationService";
import JsonData from "@/server/com/silkplm/engine/json/JsonData";
import ImageMetadata from "@/server/com/silkplm/rpc/plm/ImageMetadata";

export default {
  name: "ImageCard",
  props: {
    imageSrc: {
      type: String,
      required: true
    },
    imageAlt: {
      type: String,
      default: ""
    },
    imageIndex: {
      type: Number,
      default: 0
    },
    wishListSelectedImages: {
      type: Array,
      required: true
    },
    image: {
      type: FileInRepository,
      default: null
    },
    validatedField: {
      default: null
    },
    selected: {
      type: Boolean,
      default: false
    },
    starred: {
      type: Boolean,
      default: false
    },
    mode: {
      type: String,
      default: null
    }
  },
  async mounted() {
    this.allTagsFromDB = await ImageTag.list();
  },
  data() {
    return {
      imagePreview: true,
      showDialog: false,
      generating: false,
      imageData: null,
      progressValue: 0,
      localImagesList: [],
      allTagsFromDB: [],
      tagIconMap: {
        Text_to_image: ['fas', 'sparkles'],
        Draw_from_scratch: ['fa-regular', 'shirt'],
        Sketch_to_Image: ['fa-regular', 'shirt'],
        Fashion: ['fa-regular', 'shirt'],
        Home_Decor: ['fas', 'couch'],
        Fabric_pattern: ['far', 'game-board'],
        Accessories: ['far', 'gem'],
        Sketch: ['fas', 'pen-ruler'],
        Colored_Sketch: ['fas', 'paintbrush-pencil'],
        Photo_Realistic: ['fas', 'image'],
        Wishlist: ['far', 'square-heart'],
        "1_pose": ['fas', 'person'],
        "3_poses": ['fas', 'person-walking-arrow-loop-left'],
      }
    }
  },
  computed: {
    starState() {
      return this.starred ? 'star' : 'star-outline';
    },
    isWishlist() {
      return this.wishListSelectedImages.includes(this.imageIndex);
    },
    showTagSection() {
      return this.image.customData && this.image.customData.imageTagList && (
          (this.image.customData.imageTagList.length === 1 && this.image.customData.imageTagList[0].name.trim() !== "Main Picture")
          || this.image.customData.imageTagList.length > 1)
    }
  },
  methods: {

    toggleToWishlist() {
      this.$emit('toggle-selection-to-wishlist');
    },

    toggleStar() {
      this.$emit('toggle-star');
    },

    openDialog() {
      this.showDialog = true;
    },

    closeDialog() {
      this.showDialog = false;
    },

    async editImage() {
      let result = await (await ActivityManager.showActivity(PinturaImageEditor, {
        image: this.image,
        singleImageEditor: true,
        uploadImage: async (image) => await Rpc.BusinessEntities.uploadImage(image, "").lock()
      })).getResult();

      if (result) {
        await this.saveImageToGallery(result.newFile);
      }
    },

    async saveImageToGallery(image) {
      image.metadata = new ImageMetadata();
      this.localImagesList = [image, ...this.validatedField._value.imagesList];
      this.validatedField._value.imagesList = this.localImagesList;
      if (this.mode !== "NEW") {
        await this.validatedField.saveField();
      }
    },

    saveImage() {
      const url = !this.checkIsCordova
          ? this.image.downloadUrl
          : this.$secureUrl(this.addCordovaUrl + this.image.downloadUrl);

      window.open(url, '_blank');
    },

    capitalize(str) {
      if (!str) return ''
      return str.split(' ').map(word =>
          word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ')
    },

    async deleteImage() {
      if (await DialogManager.yesNo($tr('inspiration-sure-delete-title', 'Deleting Image'),
          $tr('inspiration-sure-delete', 'Are you sure that you want to delete this image?'))) {
        this.closeDialog();
        this.$emit('delete-image', this.image);
      }
    },


    async onAISketch() {
      this.$emit('generate-image', true);
      try {
        this.closeDialog()
        let placeholder = preparePlaceholder(this.image, this.allTagsFromDB);

        let newImageData = await AISketch({
          saveImage: () => getImageFileFromUrl(this.imageSrc),
          onProgress: (progress) => {
            this.progressValue = progress;
          },
          savePlaceholderToGallery: async () => await this.saveImageToGallery(placeholder),
        });

        if (newImageData) {
          let result = await replacePlaceholderWithImage(newImageData, this.validatedField);
          setImageTag(this.allTagsFromDB, result[1], "AI Retouch");
          await this.replaceImageOnIndex(result[0], result[1])
        }
      } finally {
        this.$emit('generate-image', false);
      }
    },

    async replaceImageOnIndex(index, newImageFile) {
      this.$set(this.validatedField._value.imagesList, index, newImageFile);
      if (this.mode !== "NEW") {
        await this.validatedField.saveField();
      }
    },

    async onSketch2Photo() {
      this.$emit('generate-image', true);
      try {
        this.closeDialog()
        let placeholder = preparePlaceholder(this.image, this.allTagsFromDB);

        const newImageData = await sketch2Photo(null, {
          saveImage: () => getImageFileFromUrl(this.imageSrc),
          onProgress: (progress) => {
            this.progressValue = progress;
          },
          savePlaceholderToGallery: async () => await this.saveImageToGallery(placeholder),
        });

        if (newImageData) {
          let result = await replacePlaceholderWithImage(newImageData, this.validatedField);
          setImageTag(this.allTagsFromDB, result[1], "Sketch to Image");
          await this.replaceImageOnIndex(result[0], result[1])
        }
      } finally {
        this.$emit('generate-image', false);
      }
    },
  }
}
</script>

<style scoped>
.custom-container {
  position: relative;
  width: 16.8rem;
  top: 0;
  height: 25rem;
  box-shadow: 2px 2px 10px rgba(0, 0, 0, 0.15);
  transition: transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.custom-container:hover {
  transform: scale(1.05);
  box-shadow: 4px 4px 15px rgba(0, 0, 0, 0.3);
}

@media screen and (max-width: 850px) {
  .custom-container {
    width: 19rem;
  }
}

.custom-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 8px;
}

.checkbox-wrapper {
  background-color: #253D5A;
  border-radius: 4px;
  padding: 2px 4px 2px 2px;
  position: absolute;
  bottom: 8px;
  left: 3px;
  display: flex;
  color: #FFFFFF;
  align-items: center;
  justify-content: center;
  gap: 4px;
  cursor: pointer;
}

.checkbox {
  width: 20px;
  height: 20px;
  appearance: none;
  -webkit-appearance: none;
  outline: none;
  border: 2px solid #4D7A98;
  border-radius: 4px;
  background-color: transparent;
  cursor: pointer;
}

.checkbox-light {
  background-color: #253D5A;
  border: 2px solid #FFFFFF;
}

.checkbox:checked {
  background-color: #FFFFFF;
}

.checkbox-light:checked::after {
  border: solid #253D5A;
  content: '';
  position: absolute;
  left: 10px;
  top: 7px;
  width: 5px;
  height: 10px;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.star-wrapper {
  position: absolute;
  top: 0;
  right: 0;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.star,
.star-outline {
  width: 30px;
  aspect-ratio: 1;
  background-color: #4D7A98;
}

.star {
  clip-path: polygon(79.90% 90.08%, 50.31% 75.00%, 21.12% 90.82%, 26.32% 58.02%, 2.26% 35.15%, 35.05% 29.96%, 49.37% 0.00%, 64.44% 29.59%, 97.35% 33.95%, 73.87% 57.43%);
}

.star-outline {
  clip-path: polygon(79.90% 90.08%, 50.31% 75.00%, 21.12% 90.82%, 26.32% 58.02%, 2.26% 35.15%, 35.05% 29.96%, 49.37% 0.00%, 64.44% 29.59%, 97.35% 33.95%, 73.87% 57.43%, 79.90% 90.08%, 50.31% 75.00%, 50.21% 66.50%, 69.73% 76.45%, 65.76% 54.90%, 81.25% 39.41%, 59.53% 36.53%, 49.59% 17.00%, 40.13% 36.77%, 18.49% 40.20%, 34.37% 55.30%, 30.94% 76.94%, 50.21% 66.50%, 69.73% 76.45%);
}

/* Style dla dialogu */
.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

.dialog-content {
  min-height: 450px;
  background: white;
  padding: 1rem;
  display: flex;
  border-radius: 8px;
  position: relative;
}

.dialog-image-wrapper {
  position: relative;
}

.dialog-img {
  max-width: 550px;
  max-height: 550px;
  border-radius: 8px;
  box-shadow: 2px 2px 10px rgba(0, 0, 0, 0.15);
  transition: transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out;
}

.dialog-close {
  position: absolute;
  top: 8px;
  right: 8px;
  cursor: pointer;
}

.dialog-buttons {
  margin-left: 1rem;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  padding-top: 30px;
  gap: 1rem;
}

.options-button {
  width: 100%;
  height: 40px;
  margin: 5px 0;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  background-color: #4D7A98;
}

.options-button:hover {
  background-color: #253D5A;
}

/* Animacja dla dialogu (fade-in/fade-out) */
.dialog-fade-enter-active,
.dialog-fade-leave-active {
  transition: opacity 0.5s ease;
}

.dialog-fade-enter,
.dialog-fade-leave-to {
  opacity: 0;
}

.tags-container {
  display: flex;
  color: white;
  padding: 5px;
  flex-wrap: wrap;
  border-radius: 4px;
}

.prompt-container {
  display: flex;
  width: 100%;
  color: #253D5A;
  background-color: white;
  padding: 5px;
  flex-wrap: wrap;
  border-radius: 8px;
}

.prompt-container::-webkit-scrollbar {
  width: 8px;
}

.prompt-container::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.prompt-container::-webkit-scrollbar-thumb {
  background: #888;
}

.prompt-container::-webkit-scrollbar-thumb:hover {
  background: #555;
}


.tag-item {
  background-color: #E0ECF5;
  display: flex;
  width: fit-content;
  flex-direction: row;
  border-radius: 50px;
  margin: 0px 2px 4px 2px;
  padding: 4px 8px;
  color: #253D5A;
  border: 1px solid #253D5A;
  font-size: 0.9rem;
}

.icon-box {
  width: 18px;
  display: flex;
  margin-right: 5px;
  align-items: center;
  justify-content: center;
}

.custom-buttons-container {
  display: flex;
  flex-direction: column;
  background-color: #E0ECF5;
  border-radius: 8px;
  width: 230px;
  padding: 5px 16px;
}

.custom-icon {
  width: 23px;
  margin-right: 5px;
}

</style>
